import type { Vec2 } from "../types";

/** Casiez et al. 2012, "1€ Filter: A Simple Speed-based Low-pass Filter for
 * Noisy Input in Interactive Systems". */
class LowPassFilter {
  private hatXPrev: number | null = null;

  filter(x: number, alpha: number): number {
    const hatX = this.hatXPrev === null ? x : alpha * x + (1 - alpha) * this.hatXPrev;
    this.hatXPrev = hatX;
    return hatX;
  }

  lastValue(): number | null {
    return this.hatXPrev;
  }
}

export interface OneEuroConfig {
  minCutoff: number;
  beta: number;
  dCutoff?: number;
}

class OneEuroFilter1D {
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;
  private readonly xFilter = new LowPassFilter();
  private readonly dxFilter = new LowPassFilter();
  private tPrev: number | null = null;

  constructor(cfg: OneEuroConfig) {
    this.minCutoff = cfg.minCutoff;
    this.beta = cfg.beta;
    this.dCutoff = cfg.dCutoff ?? 1;
  }

  private alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(x: number, tSeconds: number): number {
    let dt = this.tPrev === null ? 1 / 60 : tSeconds - this.tPrev;
    if (dt <= 0) dt = 1 / 60;
    this.tPrev = tSeconds;

    // Derivative from the previous *filtered* value (x_hat), read before this tick's
    // xFilter.filter() call below overwrites it — this matches Casiez/Roussel/Vogel's
    // reference One-Euro implementation; do not switch this to the previous raw x.
    const prevX = this.xFilter.lastValue();
    const dx = prevX === null ? 0 : (x - prevX) / dt;
    const edx = this.dxFilter.filter(dx, this.alpha(this.dCutoff, dt));
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    return this.xFilter.filter(x, this.alpha(cutoff, dt));
  }
}

/** 2D One-Euro filter: independent filters per axis, shared config, ms timestamps. */
export class OneEuro2D {
  private readonly fx: OneEuroFilter1D;
  private readonly fy: OneEuroFilter1D;

  constructor(cfg: OneEuroConfig) {
    this.fx = new OneEuroFilter1D(cfg);
    this.fy = new OneEuroFilter1D(cfg);
  }

  filter(v: Vec2, tMs: number): Vec2 {
    const tS = tMs / 1000;
    return { x: this.fx.filter(v.x, tS), y: this.fy.filter(v.y, tS) };
  }
}
